<template>
  <label
    v-if="label"
    :for="(id as string)"
    class="block text-sm font-semibold dark:text-white"
    :class="[!isError ? 'text-gray-800':'text-red-500']"
    >{{ label
    }} <span class="text-red-500">{{ required ? '*':'' }}</span></label>
    <div class="relative">
      <textarea
      :id="id"
      v-model="model"
      v-bind="$attrs"
      aria-haspopup="false"
      autocapitalize="off"
      autocomplete="off"
      autocorrect="off"
      :maxlength="maxlength"
      :disabled="props.disabled || Boolean($attrs.disabled)"
      :class="['form-textarea', sizes[size], colors[color], typesInput[typeInput], (isError) ? 'border-red-500 ring-red-500' : '']"
      v-on="eventHandlers"
      ></textarea>
        <IconErrorTag
        :is-error="isError"
        />
        <div v-if="showCharacterCount && maxlength && String(model).length > 0" class="absolute bottom-1 right-2">
          <p
          class="text-sm"
          :class="getCharacterCountColor"
          >{{ String(model).length }}/{{ maxlength }}</p>
        </div>
    </div>
    <ErrorTag
    :is-error="isError"
    :error-message="errorMessage"
    />
</template>
<script setup lang="ts">
import type { IConfCompxfuncion } from '@/modules/Administracion/interfaces';
import type { ModelValue } from '@/modules/Core/usings/constantes';
import { getEventHandlers, getIsError, generarUuid } from '@/modules/Core/usings/generales';
import type { ErrorObject } from '@vuelidate/core';
import { computed } from 'vue';

const model = defineModel<ModelValue>()
const props = defineProps<{
  id?: string
  label?: string
  color?: string
  typeInput?: string
  size?: string
  maxlength?: number
  error?: string | Array<ErrorObject> | null | undefined
  required?: boolean
  disabled?: boolean
  compxfunciones?: IConfCompxfuncion[]
  showCharacterCount?: boolean
}>()
const {
  id = generarUuid(),
  label,
  color = 'default',
  typeInput = 'default',
  size = 'default',
  required = false,
  maxlength,
  showCharacterCount = false
} = props
const errorMessage = computed(()=> props.error)
const eventHandlers = computed(() => {
  let handlers: Record<string, (e: Event) => void> = {}

  if (props.compxfunciones && props.compxfunciones.length > 0) {
    handlers = getEventHandlers(typeof(props.compxfunciones) === 'string' ? JSON.parse(props.compxfunciones): props.compxfunciones)
  }
  return handlers
})
const isError = computed(() => {
  const isArray = Array.isArray(props.error)
  return getIsError(props, isArray)
})
const sizes = {
  small: ' py-2 px-3 ',
  default: ' py-3 px-4 ',
  large: ' p-4 sm:p-5 ',
  extraLargue: 'py-3 px-4 '
} as Record<string, string>
const colors = {
  default: ' focus:border-blue-500 focus:ring-blue-500 read-only:bg-gray-100 read-only:text-gray-800 ',
  red: ' focus:border-red-500 focus:ring-red-500 ',
  blue: ' focus:border-blue-500 focus:ring-blue-500 ',
  green: ' focus:border-green-500 focus:ring-green-500 ',
  yellow: ' focus:border-yellow-500 focus:ring-yellow-500 ',
} as Record<string, string>
const typesInput = {
  default: ' py-3 px-4 block w-full border-gray-600 rounded-lg text-sm  disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-400 dark:placeholder-neutral-500 dark:focus:ring-neutral-600 ',
  grayinput: ' peer py-3 px-4 ps-11 block w-full bg-gray-100 border-transparent rounded-lg text-sm disabled:opacity-50 disabled:pointer-events-none dark:bg-gray-700 dark:border-transparent dark:text-gray-400 dark:focus:ring-gray-600 ',
} as Record<string, string>

// Computed para el color del contador de caracteres
const getCharacterCountColor = computed(() => {
  if (!maxlength || !model.value || typeof model.value !== 'string') {
    return 'text-gray-700'
  }

  const currentLength = model.value.length
  const percentage = (currentLength / maxlength) * 100

  if (percentage >= 90) {
    return 'text-green-500' // Verde cuando está al 90% o más
  } else if (percentage >= 60) {
    return 'text-yellow-500' // Amarillo cuando está al 60% o más
  } else {
    return 'text-gray-700' // Gris por defecto
  }
})
</script>
